import { Modal, Stack, Text, Button, Alert, Group } from "@mantine/core";
import { IconClock, IconLogin, IconAlertCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useFacilityContext } from "@/hooks/useFacilityContext";
import { useFacilities } from "@/hooks/useFacilities";
import { useCheckIn } from "@/hooks/useShifts";

interface ShiftRequiredModalProps {
  opened: boolean;
  pageName?: string;
  /**
   * Facility to check in to. Defaults to the resolved facility context
   * (the facility the user is currently operating in — e.g. the resident's
   * facility, selected via the top-bar FacilitySwitcher). In the presence
   * model there are no pre-scheduled shifts, so check-in is the only path.
   */
  facilityId?: string;
}

/**
 * Modal that blocks access to shift-required pages.
 *
 * Presence model (no pre-scheduled shifts): an off-duty user is offered a
 * single "Check in to <facility>" action that creates a presence shift
 * (POST /api/shifts) for the target facility. On success the auth context
 * refetches, `isOnShift` flips true, and ShiftRequiredRoute dismisses this
 * modal automatically.
 */
export function ShiftRequiredModal({
  opened,
  pageName = "this page",
  facilityId,
}: ShiftRequiredModalProps) {
  const navigate = useNavigate();
  const { currentFacilityId } = useFacilityContext();
  const checkIn = useCheckIn();

  const targetFacilityId = facilityId ?? currentFacilityId;

  // Resolve a display name for the target facility (button label).
  const { data: facilities } = useFacilities(undefined, {
    enabled: opened && !!targetFacilityId,
  });
  const facilityName =
    facilities?.find((f) => f.id === targetFacilityId)?.name ?? "this facility";

  // Checking in flips `isOnShift` (useCheckIn refetches the auth context), so
  // ShiftRequiredRoute re-renders and dismisses this modal automatically.
  const handleCheckIn = () => {
    if (targetFacilityId) checkIn.mutate({ facility_id: targetFacilityId });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleGoBack}
      title={
        <Group gap="xs">
          <IconAlertCircle size={24} color="var(--mantine-color-orange-6)" />
          <Text fw={600} size="lg">
            Active Shift Required
          </Text>
        </Group>
      }
      centered
      size="md"
      overlayProps={{
        opacity: 0.7,
        blur: 8,
      }}
      // W-6: when no facility resolves there is NO in-modal check-in action —
      // the only path forward is the header Check in button, which a non-
      // dismissable blocking overlay would cover. So make the overlay
      // dismissable in that case. With a target facility the gate stays modal.
      closeOnClickOutside={!targetFacilityId}
      closeOnEscape={!targetFacilityId}
    >
      <Stack gap="md">
        {/* Explanation */}
        <Alert
          color="orange"
          icon={<IconClock size={20} />}
          title="Shift Requirement"
        >
          <Text size="sm">You must be on duty to access {pageName}.</Text>
        </Alert>

        {/* Action */}
        <Stack gap="sm">
          {targetFacilityId ? (
            // Off duty — check in to the facility this page belongs to.
            <>
              <Button
                leftSection={<IconLogin size={18} />}
                onClick={handleCheckIn}
                loading={checkIn.isPending}
                fullWidth
                size="md"
              >
                Check in to {facilityName}
              </Button>
              <Text size="xs" c="dimmed" ta="center">
                You can also check in from the button in the top bar.
              </Text>
            </>
          ) : (
            // No resolvable facility — direct them to the top-bar control.
            <Text size="sm" ta="center">
              Go back, then use the <strong>Check in</strong> button in the top
              bar to select a facility and go on duty.
            </Text>
          )}

          <Button variant="subtle" onClick={handleGoBack} fullWidth>
            Go Back
          </Button>
        </Stack>

        {/* Additional Information */}
        <Alert
          color="blue"
          variant="light"
          icon={<IconAlertCircle size={16} />}
        >
          <Text size="xs">
            <strong>Why is this required?</strong> Care documentation and
            certain medical activities require you to be on duty at the
            resident's facility for compliance and audit purposes.
          </Text>
        </Alert>
      </Stack>
    </Modal>
  );
}
